import EzController from 'controllers/ez-base-controller';
import tinymce from 'tinymce/tinymce';
import {
  EZ_RICH_TEXT_ENABLE,
  EZ_RICH_TEXT_DISABLE,
  EZ_RICH_TEXT_CHANGE,
  EZ_RICH_TEXT_CLEAR,
  EZ_RICH_TEXT_SET,
  EZ_RICH_TEXT_SUBMIT,
} from 'lib/ez-custom-events';
import { triggerCustomEvent } from 'lib/helpers';

type RichTextConfig = {
  target:HTMLElement
  setup:(_editor: any) => void
  'base_url':string
  height?:number
  'min_height'?:number
  plugins:string
  menubar:string | boolean
  toolbar:string
  statusbar?:boolean
  'toolbar_sticky':boolean
  suffix:string
  skin?:string
  'skin_url'?:string
  'content_css'?:string
  'autoresize_bottom_margin'?:number
  'autoresize_overflow_padding'?:number
  'forced_root_block'?:string
  'force_br_newlines'?:boolean
  'force_p_newlines'?:boolean
}

export default class RichTextController extends EzController {
  declare readonly inputTarget:HTMLInputElement;

  declare autoInitValue:boolean

  declare pluginsValue:string

  declare toolbarValue:string

  declare toolbarStickyValue:boolean

  declare menubarValue:string|boolean

  declare statusbarValue:boolean

  declare heightValue:string|null

  declare minHeightValue:string|null

  declare skinUrlValue:string|null

  declare contentCssValue:string|null

  declare skinValue:string

  declare submitOnEnterValue:boolean

  declare config:RichTextConfig;

  static targets = ['input'];

  static values = {
    autoInit: { type: Boolean, default: true },
    plugins: { type: String, default: 'print preview paste importcss autolink autosave save directionality code visualblocks visualchars fullscreen image link media table charmap hr pagebreak nonbreaking anchor toc advlist lists imagetools textpattern noneditable help charmap quickbars' },
    menubar: { type: String, default: 'file edit view insert format tools table' },
    toolbar: { type: String, default: 'undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist preview | forecolor backcolor removeformat | pagebreak | charmap | insertfile image media link anchor | ltr rtl fullscreen' },
    height: String,
    minHeight: String,
    skinUrl: { type: String, default: (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'oxide-dark' : 'oxide') },
    contentCss: { type: String, default: (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'default') },
    skin: String,
    statusbar: Boolean,
    toolbarSticky: { type: Boolean, default: false },
    submitOnEnter: { type: Boolean, default: false },
  }

  initialize() {
    this.config = {
      target: this.inputTarget,
      setup: (editor) => {
        editor.on('keyup', (event) => {
          const value = event.target.innerHTML;

          triggerCustomEvent(EZ_RICH_TEXT_CHANGE, { value });
        });

        editor.on('NodeChange', () => {
          if (!tinymce.activeEditor) return;

          const value = tinymce.activeEditor.getContent();

          triggerCustomEvent(EZ_RICH_TEXT_CHANGE, { value });
        });

        if (this.submitOnEnterValue) {
          editor.on('keydown', (event) => {
            if (event.keyCode === 13) {
              event.preventDefault();

              if (event.shiftKey) {
                tinymce.activeEditor.execCommand('mceInsertNewLine');
              } else {
                triggerCustomEvent(EZ_RICH_TEXT_SUBMIT, {}, this.inputTarget);
              }
            }
          });
        }
      },
      base_url: `${process.env.ASSET_PATH}tinymce`,
      height: parseInt(this.heightValue, 10),
      min_height: parseInt(this.minHeightValue, 10),
      plugins: this.pluginsValue,
      autoresize_bottom_margin: 0,
      menubar: this.menubarValue,
      toolbar: this.toolbarValue,
      toolbar_sticky: this.toolbarStickyValue,
      statusbar: this.statusbarValue,
      suffix: '.min',
      skin_url: this.skinUrlValue,
      skin: this.skinValue,
      content_css: this.contentCssValue,
      forced_root_block: '',
      force_br_newlines: true,
      force_p_newlines: false,
    };
  }

  connect() {
    if (this.autoInitValue) {
      this.initRichText();
    } else {
      window.addEventListener(EZ_RICH_TEXT_ENABLE, this.initRichText);
      window.addEventListener(EZ_RICH_TEXT_DISABLE, this.disableRichText);
    }
  }

  disconnect() {
    this.disableRichText();
  }

  initRichText = () => {
    tinymce.init(this.config);

    window.addEventListener(EZ_RICH_TEXT_CLEAR, this.clearContent);
    window.addEventListener(EZ_RICH_TEXT_SET, this.setContent);
  }

  disableRichText = () => {
    tinymce.remove();

    window.removeEventListener(EZ_RICH_TEXT_ENABLE, this.initRichText);
    window.removeEventListener(EZ_RICH_TEXT_DISABLE, this.disableRichText);
    window.removeEventListener(EZ_RICH_TEXT_CLEAR, this.clearContent);
    window.removeEventListener(EZ_RICH_TEXT_SET, this.setContent);
  }

  clearContent() {
    tinymce.activeEditor.setContent('');
  }

  setContent(event) {
    const { content } = event.detail;

    tinymce.activeEditor.setContent(content);
  }
}
